/**
 * Fallback SSO session token cookie name.
 *
 * Used for any host that is not explicitly mapped below, such as `localhost`
 * during local development. Every deployed environment, including production,
 * resolves to its own dedicated name.
 */
export const DEFAULT_COOKIE_NAME = 'sso_session_token';

/**
 * Resolves the SSO session token cookie name for the host currently being served.
 *
 * Every environment lives under the shared apex domain `xtapestry.app`, and
 * production is served from the apex itself. A cookie set on the apex is sent by
 * the browser to every subdomain (RFC 6265), so a single shared cookie name lets a
 * production session token reach - and be rejected by - the other environments.
 * Giving each environment its own cookie name keeps those sessions isolated.
 *
 * The SSO portal (`sso.<env>.xtapestry.app`) writes the cookie and this application
 * (`<env>.xtapestry.app`) reads it, so both hosts of an environment must resolve to
 * the same name. This mapping is therefore duplicated in the `sso-login` app at
 * `apps/sso-login/src/app/utils/session-cookie.ts` and the two must be kept in step;
 * if they disagree, the application cannot see the session the portal just wrote.
 *
 * Derived synchronously from the hostname so the name is available immediately at
 * startup, before any asynchronous configuration has loaded.
 *
 * @param hostname Host to resolve; defaults to the host currently being served.
 */
export function getSessionTokenCookieName(hostname = window.location.hostname): string {
  switch (hostname) {
    case 'dev.xtapestry.app':
    case 'sso.dev.xtapestry.app':
      return 'sso_session_token_dev';

    case 'staging.xtapestry.app':
    case 'sso.staging.xtapestry.app':
      return 'sso_session_token_staging';

    case 'uat.xtapestry.app':
    case 'sso.uat.xtapestry.app':
      return 'sso_session_token_uat';

    case 'experimental-platform.xtapestry.app':
    case 'sso.experimental-platform.xtapestry.app':
    case 'maisenovich.xtapestry.app':
      return 'sso_session_token_exp';

    case 'loom.xtapestry.app':
    case 'sso.loom.xtapestry.app':
      return 'sso_session_token_loom';

    case 'xtapestry.app':
    case 'sso.xtapestry.app':
      return 'sso_session_token_prod';

    default:
      return DEFAULT_COOKIE_NAME;
  }
}
